import * as Sentry from '@sentry/react';

/**
 * Shape we read off api-client errors. Kept narrow on purpose so the handler
 * is decoupled from the concrete `ApiError` class in `@kirin/api-client`.
 */
type ApiErrorLike = {
  status: number;
  data?: unknown;
  config?: { url?: string; method?: string };
};

/**
 * Error handler for API errors — controls how Sentry sees status codes.
 *
 * - 401: dropped entirely. Owned by the dedicated session-recovery interceptor
 *   in `api-client.ts`; reporting here would just double-count auth failures.
 * - 404: still captured but demoted to `level: 'warning'`. Many endpoints in
 *   this app return 404 by design for "resource missing / not configured /
 *   feature-gated" states (e.g. `GET /organizations/:orgId/settings/siem`,
 *   expired invites — see KIR-2173) and callers already handle them via
 *   React Query `isError` +
 *   `NotFoundError`. Demoting (instead of dropping) keeps unexpected 404s —
 *   URL typos, removed routes — discoverable in Sentry without polluting the
 *   error dashboard / alert rules with the expected ones.
 * - Everything else: captured at the default error level.
 *
 * Extracted from `api-client.ts` so it can be unit-tested directly without
 * the Vite `import.meta.env` syntax that prevents Jest from importing
 * `api-client.ts`.
 */
export function handleApiError(error: unknown): void {
  if (!error || typeof error !== 'object' || !('status' in error)) {
    return;
  }

  const apiError = error as ApiErrorLike;

  if (apiError.status === 401) {
    return;
  }

  Sentry.captureException(error, {
    level: apiError.status === 404 ? 'warning' : 'error',
    tags: {
      error_type: 'api_error',
      status_code: apiError.status,
    },
    extra: {
      url: apiError.config?.url,
      method: apiError.config?.method,
      data: apiError.data,
    },
  });
}
